import { useCallback, useEffect, useRef, useState } from 'react';

// Iter 99 · Modo OFFLINE Fase 1 — detección de conexión REAL.
// navigator.onLine miente en WiFi sin salida a internet (captive/repetidor de
// sótano de hotel), así que lo combinamos con un "latido" ligero al backend
// (GET /api/ping) con timeout corto. Este hook es independiente del formulario
// a propósito: en la Fase 2, el motor de sincronización (con fotos en IndexedDB)
// consumirá la MISMA señal sin rehacer nada.

const PING_URL = `${process.env.REACT_APP_BACKEND_URL}/api/ping`;
const HEARTBEAT_MS = 15000; // latido periódico (mantiene el indicador al día)
const PING_TIMEOUT_MS = 4000; // si /api/ping no responde en 4s => sin conexión real

// status: 'online' | 'offline' | 'checking'
export function useConnectivity() {
    const [status, setStatus] = useState('checking');
    const [lastOkAt, setLastOkAt] = useState(null);
    const mountedRef = useRef(true);
    const inFlightRef = useRef(false);
    const statusRef = useRef('checking');

    const setSafe = useCallback((next) => {
        if (!mountedRef.current) return;
        statusRef.current = next;
        setStatus(next);
    }, []);

    // Real reachability probe. Returns true/false and updates status.
    const checkNow = useCallback(async () => {
        // Fast path: navigator says no network at all.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            setSafe('offline');
            return false;
        }
        if (inFlightRef.current) return statusRef.current === 'online';
        inFlightRef.current = true;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
        try {
            const res = await fetch(`${PING_URL}?t=${Date.now()}`, {
                method: 'GET',
                cache: 'no-store',
                signal: controller.signal,
            });
            const ok = res.ok;
            setSafe(ok ? 'online' : 'offline');
            if (ok && mountedRef.current) setLastOkAt(Date.now());
            return ok;
        } catch (e) {
            // Aborted (timeout) or network error => no real connectivity.
            setSafe('offline');
            return false;
        } finally {
            clearTimeout(timer);
            inFlightRef.current = false;
        }
    }, [setSafe]);

    useEffect(() => {
        mountedRef.current = true;
        checkNow();
        const iv = setInterval(checkNow, HEARTBEAT_MS);
        const onOnline = () => checkNow();      // browser regained network → confirm real reachability
        const onOffline = () => setSafe('offline'); // instant feedback on network drop
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        return () => {
            mountedRef.current = false;
            clearInterval(iv);
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
        };
    }, [checkNow, setSafe]);

    return { status, isOnline: status === 'online', checkNow, lastOkAt };
}

export default useConnectivity;
